import React, { useState } from 'react';
import { Button, Tooltip } from 'antd';

import { HAND_THUMBS_UP, HAND_THUMBS_UP_FILL } from '../icons';
import { confirmationsLabel, peopleCount, readCount } from '../../utils/confirmationValues';
import ConfirmersDrawer from './ConfirmersDrawer';

/**
 * A "Me too" element in the read view (KPR-390; docs/platform-standards.md §4
 * "A count people keep is its own control").
 *
 * The element's OWN control, never a value action: a ValueActionButton only
 * ever leaves (a call, a map, a link), and this writes. So it is an antd
 * Button beside the count —
 *
 *   [(thumbs-up) Me too]  12 people · See who
 *
 * — the thumbs-up outlined until the person has said it and filled once they
 * have, `aria-pressed` saying the same, and the element's LABEL as its words
 * ("Me too", "I see this too", "+1"). Its tooltip says what a click does, and
 * to anyone who cannot see who confirmed, that the collection's managers can
 * — and can take the list away as a file (KPR-440). That one clause is how a
 * person is told, on purpose and no louder (the owner's ruling a03): no
 * prompt, banner or email; the Terms of Service carry the rest.
 * The count sits beside it and says nothing at zero (the counts rule, §9).
 * Managers (`canSeeWho`) get "See who", the panel of people (ConfirmersDrawer).
 *
 * The button shows only when the person may press it: `canConfirm`, or they
 * already confirmed (a person may always take theirs back). With no `state`
 * — a public page, a printed sheet, an API without the door, an answer still
 * on its way — the count reads alone, from the item: "—" at zero, as any
 * empty value in the read view.
 */
export default function ConfirmationsValue({ element, value, state = null, onToggle = null, itemId = null }) {
	const [whoOpen, setWhoOpen] = useState(false);
	const label = confirmationsLabel(element) || 'Me too';
	const count = state ? readCount(state.count) : readCount(value);
	const counted = peopleCount(count);
	const showButton = !!state && typeof onToggle === 'function' && (state.canConfirm || state.confirmed);

	if (!showButton) {
		return (
			<div className="item-pane__value confirm-value" data-testid="confirm-value">
				<span className="confirm-value__count">{counted || '—'}</span>
				{state?.canSeeWho && count > 0 ? (
					<SeeWho onOpen={() => setWhoOpen(true)} />
				) : null}
				{whoOpen ? (
					<ConfirmersDrawer open={whoOpen} onClose={() => setWhoOpen(false)} itemId={itemId} element={element?.name} label={label} />
				) : null}
			</div>
		);
	}

	const confirmed = state.confirmed === true;
	const action = confirmed ? `Take back your “${label}”` : `Say “${label}”`;
	const tooltip = state.canSeeWho
		? action
		: <>{action}<br />The people who manage this collection can see and export who confirmed.</>;
	const Icon = confirmed ? HAND_THUMBS_UP_FILL : HAND_THUMBS_UP;

	return (
		<div className="item-pane__value confirm-value" data-testid="confirm-value">
			<Tooltip title={tooltip}>
				<Button
					className={`confirm-value__toggle${confirmed ? ' confirm-value__toggle--on' : ''}`}
					aria-pressed={confirmed}
					icon={<Icon aria-hidden="true" />}
					onClick={(event) => { event.stopPropagation(); onToggle(element?.name); }}
				>
					{label}
				</Button>
			</Tooltip>
			{counted ? <span className="confirm-value__count">{counted}</span> : null}
			{state.canSeeWho && count > 0 ? <SeeWho onOpen={() => setWhoOpen(true)} /> : null}
			{whoOpen ? (
				<ConfirmersDrawer open={whoOpen} onClose={() => setWhoOpen(false)} itemId={itemId} element={element?.name} label={label} />
			) : null}
		</div>
	);
}

function SeeWho({ onOpen }) {
	return (
		<Button
			type="link"
			size="small"
			className="confirm-value__who"
			onClick={(event) => { event.stopPropagation(); onOpen(); }}
		>
			See who
		</Button>
	);
}
